/**
 * The Blinkit-theme "Price details" lines, read from the server's cart / order totals (routers/customer/cart.py
 * ``compute_cart_totals`` → ``Order.totals``). Nothing is priced here: every amount is a server field, the only
 * arithmetic is picking / labelling them.
 *
 *     Selling price        subtotal                         (pricing-engine line totals)
 *   − Discount / Coupon    discount                         (coupon, except FREE_DELIVERY, + coins)
 *   + GST                  gst_total                        (items + delivery + taxable charges)
 *   + Platform charges     additional_charges_total         (tenant's additional charges, named)
 *   + Packaging            packaging_fee                    (only when > 0)
 *   + Delivery fee         delivery_fee                     (FREE when 0 — a FREE_DELIVERY coupon lands here)
 *   = Total                grand_total
 *
 * ``gst_total`` is exposed by the server so that ``gst_total + additional_charges_total == gst_and_other_charges``
 * exactly; for orders placed before that field existed the same split is taken from those two stored numbers.
 */
const r2 = (v) => Math.round((Number(v) || 0) * 100) / 100;

export function gstOf(t) {
    if (t.gst_total != null) return r2(t.gst_total);
    if (t.gst_and_other_charges != null) return r2(Number(t.gst_and_other_charges) - Number(t.additional_charges_total || 0));
    return r2(Number(t.tax || 0) + Number(t.delivery_gst || 0));
}

export function priceBreakdown(totals) {
    const t = totals || {};
    const subtotal = r2(t.subtotal);
    // The server floors (subtotal − discount) at 0, so the bill never shows more discount than the items cost.
    const discount = Math.min(r2(t.discount), subtotal);
    const gst = gstOf(t);
    const charges = r2(t.additional_charges_total);
    const packaging = r2(t.packaging_fee);
    const delivery = r2(t.delivery_fee);
    const total = r2(t.grand_total);

    const discountParts = [];
    if (t.coupon_code && Number(t.coupon_discount) > 0 && t.coupon_kind !== "FREE_DELIVERY") discountParts.push(`Coupon ${t.coupon_code}`);
    if (Number(t.coins_discount) > 0) discountParts.push(`${t.coins_used || ""} coins`.trim());

    const lines = [
        { key: "selling", label: "Selling price", amount: subtotal },
        { key: "discount", label: "Discount / Coupon", amount: -discount, note: discountParts.join(" · ") || null },
        { key: "gst", label: "GST", amount: gst },
        {
            key: "platform", label: "Platform charges", amount: charges,
            note: (t.additional_charges || []).map((c) => c.name).filter(Boolean).join(", ") || null,
        },
        ...(packaging > 0 ? [{ key: "packaging", label: "Packaging", amount: packaging }] : []),
        {
            key: "delivery", label: "Delivery fee", amount: delivery,
            free: delivery === 0,
            struck: delivery === 0 && Number(t.delivery_fee_rider) > 0 ? r2(t.delivery_fee_rider) : null,
            note: delivery === 0 && t.free_delivery_source === "COUPON" ? "Free delivery coupon"
                : delivery === 0 && t.free_delivery_source === "ZONE" ? "Free delivery in your area"
                    : delivery === 0 && t.free_delivery_source === "SHOP" ? "Free delivery from this store" : null,
        },
    ];
    const sum = r2(lines.reduce((s, l) => s + l.amount, 0));
    const savings = r2(discount + (lines.find((l) => l.key === "delivery").struck || 0));
    return { lines, total, sum, balanced: Math.abs(sum - total) <= 0.011, savings };
}

/**
 * An ORDER snapshot stores ``grand_total`` AFTER the wallet was debited (routers/customer/checkout.py), while a cart's
 * ``grand_total`` is before it. For the order bill: Total = grand_total + wallet_used; "To pay" = grand_total.
 */
export function orderBillTotals(orderTotals) {
    const t = orderTotals || {};
    const wallet = r2(t.wallet_used);
    return { totals: { ...t, grand_total: r2(Number(t.grand_total || 0) + wallet) }, walletApplied: wallet };
}

export const fmtInr = (n) => {
    const v = Number(n || 0);
    return `₹${v.toLocaleString("en-IN", { minimumFractionDigits: Number.isInteger(v) ? 0 : 2, maximumFractionDigits: 2 })}`;
};
